export type StreetAddress = {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  lat: number | null;
  lng: number | null;
};

export type PlaceAddressComponent = {
  types: string[];
  longText?: string;
  shortText?: string;
  long_name?: string;
  short_name?: string;
};

export type PlaceAddressSource = {
  formattedAddress?: string | null;
  formatted_address?: string | null;
  addressComponents?: PlaceAddressComponent[] | null;
  address_components?: PlaceAddressComponent[] | null;
  location?:
    | { lat: number; lng: number }
    | { lat: () => number; lng: () => number }
    | null;
};

function textOf(component: PlaceAddressComponent | undefined, short: boolean): string {
  if (!component) return "";
  if (short) return (component.shortText ?? component.short_name ?? component.longText ?? component.long_name ?? "").trim();
  return (component.longText ?? component.long_name ?? component.shortText ?? component.short_name ?? "").trim();
}

function componentNamed(components: PlaceAddressComponent[], type: string): PlaceAddressComponent | undefined {
  return components.find((component) => component.types.includes(type));
}

function coord(location: PlaceAddressSource["location"], key: "lat" | "lng"): number | null {
  if (!location || typeof location !== "object") return null;
  const raw = (location as Record<string, unknown>)[key];
  if (typeof raw === "function") {
    const value = (raw as () => unknown).call(location);
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

const US_STATE_NAMES: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "district of columbia": "DC",
};

function stateAndZip(region: string): { state: string; postalCode: string } | null {
  const withZip =
    region.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/) ??
    region.match(/^(.+?)\s+(\d{5}(?:-\d{4})?)$/);
  if (withZip) {
    const rawState = withZip[1] ?? "";
    const named = US_STATE_NAMES[rawState.toLowerCase()];
    const state = named ?? (rawState.length === 2 ? rawState.toUpperCase() : rawState);
    return {
      state: state.length === 2 ? state.toUpperCase() : state,
      postalCode: withZip[2] ?? "",
    };
  }
  if (/^[A-Za-z]{2}$/.test(region)) return { state: region.toUpperCase(), postalCode: "" };
  const named = US_STATE_NAMES[region.toLowerCase()];
  if (named) return { state: named, postalCode: "" };
  return null;
}

function isCountryPart(part: string): boolean {
  return /^(usa|us|united states|united states of america)$/i.test(part.trim());
}

function isPostalCodePart(part: string): boolean {
  return /^\d{5}(?:-\d{4})?$/.test(part.trim());
}

function parseFormattedAddress(
  formatted: string,
): Pick<StreetAddress, "addressLine1" | "city" | "state" | "postalCode"> | null {
  const parts = formatted
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 3) return null;
  let locality = isCountryPart(parts[parts.length - 1] ?? "") ? parts.slice(0, -1) : parts;
  let postalFromPart = "";
  // Closed-shadow walks join each text node with a comma, so ZIP often lands
  // on its own part: "1826 Lucretia Ave, Los Angeles, CA, 90026".
  while (locality.length >= 4 && isPostalCodePart(locality[locality.length - 1] ?? "")) {
    postalFromPart = locality[locality.length - 1] ?? postalFromPart;
    locality = locality.slice(0, -1);
  }
  if (locality.length < 3) return null;
  const region = locality[locality.length - 1] ?? "";
  const city = locality[locality.length - 2] ?? "";
  const street = locality.slice(0, -2).join(", ");
  const parsedRegion = stateAndZip(region);
  if (!street || !city || !parsedRegion) return null;
  return {
    addressLine1: street,
    city,
    state: parsedRegion.state,
    postalCode: parsedRegion.postalCode || postalFromPart,
  };
}

export function parsePlaceAddress(place: PlaceAddressSource): StreetAddress | null {
  const components = place.addressComponents ?? place.address_components ?? [];
  const formatted = (place.formattedAddress ?? place.formatted_address ?? "").trim();
  const lat = coord(place.location ?? null, "lat");
  const lng = coord(place.location ?? null, "lng");
  if (components.length) {
    const streetNumber = textOf(componentNamed(components, "street_number"), false);
    const route = textOf(componentNamed(components, "route"), false);
    const subpremise = textOf(componentNamed(components, "subpremise"), false);
    const city =
      textOf(componentNamed(components, "locality"), false) ||
      textOf(componentNamed(components, "sublocality"), false) ||
      textOf(componentNamed(components, "postal_town"), false);
    const state = textOf(componentNamed(components, "administrative_area_level_1"), true);
    const postal = textOf(componentNamed(components, "postal_code"), false);
    const suffix = textOf(componentNamed(components, "postal_code_suffix"), false);
    const line1 = [streetNumber, route].filter(Boolean).join(" ") || formatted.split(",")[0]?.trim() || "";
    if (line1 && city) {
      return {
        addressLine1: line1,
        addressLine2: subpremise || null,
        city,
        state,
        postalCode: suffix ? `${postal}-${suffix}` : postal,
        lat,
        lng,
      };
    }
  }
  if (!formatted) return null;
  const parsed = parseFormattedAddress(formatted);
  if (!parsed) return null;
  return { ...parsed, addressLine2: null, lat, lng };
}

const closedShadows = new WeakMap<Element, ShadowRoot>();

export function registerClosedShadow(host: Element, root: ShadowRoot): void {
  closedShadows.set(host, root);
}

export function elementShadowRoot(element: Element): ShadowRoot | null {
  return element.shadowRoot ?? closedShadows.get(element) ?? null;
}

export function readDeepText(root: Node): string {
  const chunks: string[] = [];
  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.replace(/\s+/g, " ").trim();
      if (text) chunks.push(text);
      return;
    }
    if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      for (const child of Array.from(node.childNodes)) visit(child);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as Element;
    const shadow = elementShadowRoot(element);
    if (shadow) visit(shadow);
    for (const child of Array.from(element.childNodes)) visit(child);
  };
  visit(root);
  return chunks.join(", ");
}

export function normalizeAddressText(value: string): string {
  return String(value ?? "")
    .replace(/\s*\n+\s*/g, ", ")
    .replace(/\s+/g, " ")
    .replace(/(?:,\s*){2,}/g, ", ")
    .trim();
}

function addressNodeLabel(node: Element): string {
  const record = node as HTMLElement & {
    formattedAddress?: unknown;
    formatted_address?: string | null;
    address?: unknown;
  };
  const fromFields = [
    typeof record.formattedAddress === "string" ? record.formattedAddress : "",
    record.formatted_address ?? "",
    typeof record.address === "string" ? record.address : "",
  ];
  for (const value of fromFields) {
    const text = value.trim();
    if (text) return text;
  }
  return (
    fromFields.find((value) => value.trim()) ||
    readDeepText(node) ||
    (node instanceof HTMLElement ? node.innerText ?? "" : "")
  );
}

export function parseRenderedPlaceAddress(
  host: HTMLElement,
  place?: PlaceAddressSource | null,
): StreetAddress | null {
  const fromPlace = place ? parsePlaceAddress(place) : null;
  if (fromPlace?.addressLine1 && fromPlace.city && zipKey(fromPlace.postalCode)) return fromPlace;

  const root = elementShadowRoot(host);
  const addressNodes = [
    ...Array.from(host.querySelectorAll("gmp-place-address")),
    ...Array.from(root?.querySelectorAll("gmp-place-address") ?? []),
  ];
  const candidates = [
    ...addressNodes.map((node) => addressNodeLabel(node)),
    readDeepText(host),
    host.innerText ?? "",
  ];
  let best = fromPlace;
  for (const text of candidates) {
    const formatted = normalizeAddressText(text);
    if (!formatted) continue;
    const parsed = parsePlaceAddress({
      formattedAddress: formatted,
      location: place?.location ?? null,
    });
    const zip = usPostalCodeInText(formatted, parsed?.addressLine1 || best?.addressLine1);
    if (parsed) {
      const withZip = parsed.postalCode || !zip ? parsed : { ...parsed, postalCode: zip };
      if (zipKey(withZip.postalCode)) return withZip;
      if (!best) best = withZip;
    } else if (zip && best && !zipKey(best.postalCode)) {
      best = { ...best, postalCode: zip };
    }
  }
  return best;
}

export function zipKey(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "").slice(0, 5);
}

export function usPostalCodeInText(value: string, street?: string): string {
  const text = normalizeAddressText(value);
  const afterState = text.match(/\b[A-Za-z]{2}\s+(\d{5}(?:-\d{4})?)\b/);
  if (afterState?.[1]) return afterState[1];
  const streetDigits = (street ?? "").match(/^\d+/)?.[0] ?? "";
  const matches = text.match(/\b(\d{5}(?:-\d{4})?)\b/g) ?? [];
  for (const raw of matches) {
    const five = raw.replace(/\D/g, "").slice(0, 5);
    if (five && five !== streetDigits) return raw;
  }
  return "";
}

export function streetKey(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/[.,#]/g, "").replace(/\s+/g, " ");
}

export function sameStreetAndZip(
  left: { addressLine1: string; postalCode?: string | null },
  right: { addressLine1: string; postalCode?: string | null },
): boolean {
  return streetKey(left.addressLine1) === streetKey(right.addressLine1) && zipKey(left.postalCode) === zipKey(right.postalCode) && zipKey(left.postalCode).length > 0;
}
